import { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import WebcamCapture from '../components/WebcamCapture';

const PersonasPortero = () => {
    const toast = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const HOST = HOST_API?.replace(/\/+$/, '');
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const [personasCombo, setPersonasCombo] = useState([]);
    const [personaSelId, setPersonaSelId] = useState(null);
    const [fotos, setFotos] = useState({});

    const [webcamVisible, setWebcamVisible] = useState(false);
    const [personaSel, setPersonaSel] = useState(null);
    const [subiendoFoto, setSubiendoFoto] = useState(false);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/personas/GetPersonasCombo?administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setPersonasCombo(data ?? []))
            .catch(() => {});
    }, [HOST_API, barrio?.id, administracionId]);

    const fetchFotos = useCallback(async () => {
        if (!barrio?.id) return;
        try {
            const { data } = await axios.get(`${HOST_API}api/personas/GetFotosPersonas?administracionId=${administracionId}&personaId=0&barrioId=${barrio.id}`);
            const mapa = {};
            (data ?? []).forEach((f) => { mapa[f.personaId] = f.fotoUrl; });
            setFotos(mapa);
        } catch { /* silencioso */ }
    }, [HOST_API, barrio?.id, administracionId]);

    useEffect(() => { fetchFotos(); }, [fetchFotos]);

    const handlePersonaChange = (personaId) => {
        setPersonaSelId(personaId);
    };

    const personaElegida = personasCombo.find((p) => p.personaId === personaSelId) || null;

    const abrirCaptura = (persona) => {
        setPersonaSel(persona);
        setWebcamVisible(true);
    };

    const subirFoto = async (blob) => {
        if (!personaSel) return;
        setSubiendoFoto(true);
        try {
            const fd = new FormData();
            fd.append('file', blob, 'foto.jpg');
            const { data } = await axios.post(
                `${HOST_API}api/personas/UploadFoto?personaId=${personaSel.personaId}&administracionId=${administracionId}&barrioId=${barrio.id}`,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setFotos((prev) => ({ ...prev, [personaSel.personaId]: data.fotoUrl }));
            setWebcamVisible(false);
            toast.current.show({ severity: 'success', summary: 'Foto guardada', detail: `Se asoció la foto a ${personaSel.nombreCompleto}`, life: 2500 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar la foto', life: 3000 });
        } finally {
            setSubiendoFoto(false);
        }
    };

    return (
        <>
            <h4>Registro de Rostros</h4>
            <p style={{ color: '#667085', fontSize: '0.9rem', marginTop: '-0.5rem' }}>
                Buscá una persona para registrarle una foto de rostro.
            </p>
            <br />
            <Toast ref={toast} />

            <div className="flex align-items-center gap-3 mb-4">
                <Dropdown
                    value={personaSelId}
                    options={personasCombo}
                    optionLabel="nombreCompleto"
                    optionValue="personaId"
                    onChange={(e) => handlePersonaChange(e.value)}
                    placeholder="Buscá una persona"
                    filter
                    className="p-inputtext-sm"
                    style={{ minWidth: 260 }}
                />
            </div>

            {!personaElegida ? (
                <p style={{ color: '#98a2b3' }}>Elegí una persona para empezar.</p>
            ) : (
                <div className="flex flex-wrap gap-3">
                    <div className="card" style={{ width: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', padding: '1.25rem' }}>
                        {fotos[personaElegida.personaId]
                            ? <img src={`${HOST}${fotos[personaElegida.personaId]}`} alt="" style={{ width: 84, height: 84, borderRadius: '50%', objectFit: 'cover' }} />
                            : <div style={{ width: 84, height: 84, borderRadius: '50%', background: '#f2f4f7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#98a2b3' }}><i className="pi pi-user" style={{ fontSize: '2rem' }} /></div>
                        }
                        <span style={{ fontWeight: 600, textAlign: 'center' }}>{personaElegida.nombreCompleto}</span>
                        <Button
                            label={fotos[personaElegida.personaId] ? 'Reemplazar foto' : 'Tomar foto'}
                            icon="pi pi-camera"
                            size="small"
                            severity="secondary"
                            outlined
                            onClick={() => abrirCaptura(personaElegida)}
                        />
                    </div>
                </div>
            )}

            <WebcamCapture
                visible={webcamVisible}
                onHide={() => setWebcamVisible(false)}
                onCapture={subirFoto}
                header={subiendoFoto ? 'Guardando...' : `Foto de ${personaSel?.nombreCompleto ?? ''}`}
            />
        </>
    );
};

export default PersonasPortero;
